/**
 * Implements a trading box: balance, input, coin selector
 */
import { useEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import BigNumber from 'bignumber.js';
import { Network } from '@kattana-io/common';
import Image from '../atoms/Image';
import Text from '../atoms/Text';
import SmallCurrencyIcon from '../components/Trading/common/SmallCurrencyIcon';
import DebounceInput from '../components/Trading/common/DebounceInput';
import NumberDisplay from './NumberDisplay';
import arrow_down from '../assets/images/shevron_down.svg';

interface IProps {
  network?: Network;
  title?: string;
  estimation?: boolean;
  balance?: number;
  coinTitle: string;
  coinFullName?: string;
  coinAddress?: string;
  value?: BigNumber;
  valueUSD?: number;
  // eslint-disable-next-line no-unused-vars
  onChange: (val: any) => void;
  setTab?: () => void;
  onAllowance?: () => any;
  disabled?: boolean;
  isCoinChangable: boolean;
  className?: string;
  placeholder?: string;
}

const TradingBox = ({
  network,
  title,
  estimation,
  balance,
  coinTitle,
  coinFullName,
  coinAddress,
  value,
  valueUSD,
  onChange,
  setTab,
  onAllowance,
  disabled,
  isCoinChangable,
  className,
  placeholder,
}: IProps) => {
  useEffect(() => {
    ReactTooltip.rebuild();
  }, [onAllowance]);

  return (
    <div
      className={`trading_box ${disabled ? 'trading_box--disabled' : ''} ${
        estimation ? 'trading_box--estimation' : ''
      } ${className ? className : ''}`}
      aria-label={title}
    >
      {balance !== undefined && (
        <div className='row balance_row'>
          <div className='direction' aria-label='Coin direction'>
            <Text size={12}>{title}</Text>
          </div>
          <div
            className='balance'
            aria-label='Amount of this coin'
            onClick={() => onChange(balance)}
          >
            <Text size={12} className='text--weight-500'>
              Balance:
            </Text>
            &nbsp;
            <NumberDisplay
              value={balance}
              textProps={{ size: 12, className: 'text--grey-400' }}
            />
          </div>
        </div>
      )}
      <div className='row input_row'>
        <div className='coin_row' aria-label='Change coin' onClick={setTab}>
          {network && (
            <SmallCurrencyIcon
              network={network}
              className='coin_row__icon'
              currency={coinTitle}
              contract={coinAddress || ''}
              variant='s'
            />
          )}
          <Text
            className='coin_row__title'
            size={18}
            style={{ title: coinTitle }}
          >
            {coinTitle}
          </Text>
          {isCoinChangable && <Image src={arrow_down} size='m' />}
          {onAllowance && (
            <div
              className='coin_row__allowance icon-lock'
              onClick={(e) => {
                e.stopPropagation();
                onAllowance();
              }}
              data-for='trading-box-tooltip'
              data-tip='Give permission to swap token'
            />
          )}
        </div>
        <DebounceInput
          value={value}
          onChange={onChange}
          className='input'
          disabled={disabled ?? false}
          placeholder={placeholder}
        />
      </div>
      {(coinFullName || valueUSD) && (
        <div className='row additional_info'>
          <Text size={12}>{coinFullName}</Text>
          <Text size={12}>
            ~ $&nbsp;
            <NumberDisplay
              value={valueUSD}
              textProps={{ size: 12, className: 'text--grey-400' }}
            />
          </Text>
        </div>
      )}
      <ReactTooltip
        id='trading-box-tooltip'
        type='light'
        place='top'
        effect='solid'
        className='text_tooltip'
      />
    </div>
  );
};

export default TradingBox;
